import { Store } from '@tanstack/react-store'
import type { User, Session } from '@supabase/supabase-js'
import { toast } from 'sonner'
import { supabase } from '../lib/supabase'
import { queryClient } from '../integrations/tanstack-query/root-provider'
import { setSentryUser, clearSentryUser } from '../lib/sentry'
import { isDemoModeActive } from '../lib/demo-mode'
import { oauthSignInCredentials, type OAuthProvider } from '../lib/oauth-providers'

export interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
}

export const authStore = new Store<AuthState>({
  user: null,
  session: null,
  isLoading: true,
})

// Resolves once the initial supabase.auth.getSession() call has settled
// (success or failure), so route loaders can await session availability
// before firing authenticated RPCs instead of gating on component mount.
// See _app.tsx's loader.
let resolveAuthReady: () => void
export const authReady = new Promise<void>((resolve) => {
  resolveAuthReady = resolve
})

// Initialize auth state
supabase.auth
  .getSession()
  .then(async ({ data: { session }, error }) => {
    console.log('Initial session:', session)

    // Only log actual errors, don't sign out - let autoRefreshToken handle refresh
    if (error) {
      console.error('Error getting session:', error)
    }

    authStore.setState((state) => ({
      ...state,
      session,
      user: session?.user ?? null,
      isLoading: false,
    }))

    // Set Sentry user context if already logged in
    if (session?.user) {
      setSentryUser({
        id: session.user.id,
        email: session.user.email,
      })
    }
  })
  .catch((err) => {
    console.error('Failed to initialize auth session:', err)
    authStore.setState((state) => ({ ...state, isLoading: false }))
  })
  .finally(() => {
    resolveAuthReady()
  })

// Supabase fires SIGNED_IN on every session recovery — initial page load, tab
// refocus, token refresh's _recoverAndRefresh path — not just on actual logins.
// Invalidating every query on each one caused a fan-out refetch storm on flaky
// mobile networks (CORTADO-FRONTEND-1E/1A/etc). Track the last known user so we
// only refetch on a real user→user transition; mount-time fetches cover initial
// load, so the first SIGNED_IN (previousUserId === null) is a no-op.
let lastKnownUserId: string | null = null

// Listen for auth state changes
supabase.auth.onAuthStateChange(async (event, session) => {
  try {
    console.log('Auth state changed:', event, session)

    // If token refresh failed (session is null after refresh attempt), sign out
    if (event === 'TOKEN_REFRESHED' && !session && !isDemoModeActive()) {
      await supabase.auth.signOut()
      toast.error('Session expired. Please log in again.')
    }

    const previousUserId = lastKnownUserId
    const newUserId = session?.user?.id ?? null
    lastKnownUserId = newUserId

    authStore.setState((state) => ({
      ...state,
      session,
      user: session?.user ?? null,
      isLoading: false,
    }))

    // Manage user query cache based on auth state
    if (event === 'SIGNED_IN' && session) {
      // Set Sentry user context
      setSentryUser({
        id: session.user.id,
        email: session.user.email,
      })

      // Only invalidate on a real transition between two known users (account
      // switch). Initial sign-in and session recovery should not invalidate —
      // mount-time fetches and TanStack Query's focus refetch cover those.
      if (previousUserId !== null && previousUserId !== newUserId) {
        queryClient.invalidateQueries()
      }
    } else if (event === 'SIGNED_OUT' || !session) {
      // Clear Sentry user context
      clearSentryUser()

      // Clear all queries on sign out
      queryClient.clear()
    }
  } catch (err) {
    console.error('Auth state change handler failed:', err)
  }
})

// Helper function to get app URL
function getAppUrl(): string {
  // Use environment variable if set, otherwise use current origin
  return import.meta.env.VITE_APP_URL || window.location.origin
}

// Auth actions
export const authActions = {
  signIn: async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
  },

  signUp: async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    })
    if (error) throw error
  },

  signInWithOAuthProvider: async (provider: OAuthProvider) => {
    const redirectUrl = `${getAppUrl()}/auth/callback`
    const { error } = await supabase.auth.signInWithOAuth(oauthSignInCredentials(provider, redirectUrl))
    if (error) throw error
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  resetPasswordForEmail: async (email: string) => {
    const redirectUrl = `${getAppUrl()}/reset-password`
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    })
    if (error) throw error
  },

  updatePassword: async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })
    if (error) throw error
  },

  signInWithMagicLink: async (email: string) => {
    const redirectUrl = `${getAppUrl()}/auth/callback`
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: redirectUrl,
      },
    })
    if (error) throw error
  },
}